import { v4 as uuidv4 } from 'uuid';

export const uuid = (): string => uuidv4();

type StorageType = 'local' | 'session';

export function getOrSetUniqueIdentifier(
	key: string,
	storageType: StorageType = 'local',
	identifierType?: 'uuid' | 'time' | 'random',
): string {
	const storage = storageType === 'local' ? localStorage : sessionStorage;
	let identifier = storage.getItem(key);

	if (!identifier) {
		switch (identifierType) {
			case 'time':
				identifier = Date.now().toString();
				break;
			case 'random':
				identifier = Math.random().toString();
				break;
			case 'uuid':
			default:
				identifier = uuid();
				break;
		}
		storage.setItem(key, identifier);
	}

	return identifier;
}

export function isNonEmptyObject(val: any): boolean {
	return (
		typeof val === 'object' &&
		val !== null &&
		!Array.isArray(val) &&
		Object.keys(val).length > 0
	);
}

export function isNonEmptyArray(val: any): boolean {
	return Array.isArray(val) && val.length > 0;
}

export function isTruthyPrimitive<T = unknown>(val: T): boolean {
	return typeof val !== 'object' && !!val;
}

type DeepCheckFn<T> = (val: T) => boolean;

interface HasItemOptions<T> {
	key: string;
	storageType?: StorageType;
	deepCheck?: boolean | DeepCheckFn<T>;
	checkFalsyPrimitive?: boolean;
}

export function hasItemInStorage<T>({
	key,
	storageType = 'local',
	deepCheck = false,
	checkFalsyPrimitive = false,
}: HasItemOptions<T>): boolean {
	const storage = storageType === 'local' ? localStorage : sessionStorage;

	try {
		const item = storage.getItem(key);
		if (!item) return false;

		const parsed = JSON.parse(item) as T;

		if (typeof deepCheck === 'function') {
			return deepCheck(parsed);
		}

		if (!deepCheck) {
			if (checkFalsyPrimitive && typeof parsed !== 'object') {
				return isTruthyPrimitive(parsed);
			}
			return true;
		}

		if (isNonEmptyArray(parsed)) return true;
		if (isNonEmptyObject(parsed)) return true;

		return checkFalsyPrimitive ? isTruthyPrimitive(parsed) : true;
	} catch {
		return false;
	}
}

export function safeParse<T>(text: string): T | null {
	try {
		return JSON.parse(text) as T;
	} catch (e) {
		return null;
	}
}
